import React, { useState, useEffect, FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AxiosResponse } from 'axios';
import moment from 'moment';

import ApplicationDetailsComp from './application-details';
import DropdownComp, { DropdownList } from '../../../shared/dropdown';
import { Application } from '../../../common/application';
import { RootState } from '../../../store';
import AppTable, { TableHeader } from '../../../shared/app-table';
import { ApiResponse } from '../../../common';
import { DELETE_APPLICATION, RETREIVE_APPLICATION } from '../../../services/applications';
import { INITIALIZE_APPLICATIONS, REMOVE_APPLICATION } from '../../../store/application';
import { CloseAppModal, OpenAppModal } from '../../../store/modal';
import Card from '../../../shared/card';
import DeleteComp from '../../../shared/delete-comp/delete-comp';
import AppModalComp from '../../../shared/app-modal';
import { getItem } from '../../../utils';
import JobApplicationForm from '../../../pages/job-detail-page/JobApplicationForm';

const ApplicationComp: FC = () => {
    const dispatch = useDispatch();
    const Applications: Application[] = useSelector((state: RootState) => state.applicationState.value);
    const user = getItem('clientD');

    const [deleting, setDeleting] = useState<boolean>(false);
    const [applicationsData, setApplicationsData] = useState<Application[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<Application | null>(null);
    const [modalMode, setModalMode] = useState<string>('');
    const [tableRows, setTableRows] = useState<any[]>([]);


    const notify = (type: string, msg: string) => {
        if (type === "success") {
        toast.success(msg, {
            position: toast.POSITION.TOP_RIGHT,
        });
        }

        if (type === "error") {
        toast.error(msg, {
            position: toast.POSITION.TOP_RIGHT,
        });
        }
    };

    const tableHeaders: TableHeader[] = [
        { key: 'sn', value: 'S/N' },
        { key: 'code', value: 'Application Code' },
        { key: 'name', value: 'Name' },
        { key: 'role', value: 'Role' },
        { key: 'certLevel', value: 'Qualification' },
        { key: 'status', value: 'Status' },
        { key: 'date', value: 'Date' },
        { key: 'actions', value: 'Actions' },
    ];

    const openModal = useCallback((mode: string = 'create') => {
        setModalMode(mode);
        dispatch(OpenAppModal());
    }, [dispatch]);

    const populateActions = useCallback((item: Application): DropdownList[] => {
        const tableActions: DropdownList[] = [
            { 
                label: 'View Detail', 
                disabled: false,
                action: () => {
                    setSelectedRecord(item);
                    openModal('view');
                }
            },
            { 
                label: 'Update Record', 
                disabled: false,
                action: () => {
                    setSelectedRecord(item);
                    openModal('update');
                }
            },
            { 
                label: 'Delete Record', 
                disabled: false,
                action: () => {
                    setSelectedRecord(item);
                    openModal('delete');
                }
            },
        ];
        return tableActions;
    }, [openModal]);

    const mapResponseData = useCallback((data: Application[]) => {
        const mappedData = data.map((item: Application, idx: number) => {
            const actions = populateActions(item);
            return {
                sn: idx + 1,
                code: item?.code,
                name: `${item?.firstName} ${item?.lastName}`,
                certLeve: item?.certLevel,
                role: item?.role || '--',
                certLevel: item?.certLevel || '--',
                status: item.status === 'ACCEPTED' ? 
                <button className='bg-[#71DD37] text-white text-sm py-1 px-4 rounded-md'>{item.status}</button>
                :
                <button className='bg-[#7F7F80] text-white text-sm py-1 px-4 rounded-md'>{item.status}</button>,
                date: moment(item?.createdAt).format("MM-DD-YYYY"),
                actions: <DropdownComp dropdownList={actions} />
            }
        });
        return mappedData;
    }, [populateActions]);

    const retrieveApplications = useCallback(() => {
        const query: string = `?email=${user ? user.email : ''}&sort=-createdAt&populate=job,createdBy`;
        RETREIVE_APPLICATION(query)
        .then((res: AxiosResponse<ApiResponse>) => {
            const { payload } = res.data;
            setApplicationsData(payload);
            setTableRows(mapResponseData(payload));
            dispatch(INITIALIZE_APPLICATIONS(payload));
        })
        .catch((err: any) => {
            const { message } = err.response.data;
            notify("error", message);
        });
    }, [user, mapResponseData, dispatch]);

    const handleDeleteRecord = (id: string) => {
        setDeleting(true);
        DELETE_APPLICATION(id)
        .then((res: AxiosResponse<ApiResponse>) => {
            const { message, payload, success } = res.data;
            if(success){
                setDeleting(false);
                notify("success", message);
                dispatch(REMOVE_APPLICATION(payload.id));
                dispatch(CloseAppModal());
            }
        })
        .catch((err: any) => {
            setDeleting(false);
            const { message } = err.response.data;
            notify("error", message);
        });
    }
    
    useEffect(() => {
        retrieveApplications();
    }, [retrieveApplications]);

    useEffect(() => {
        setApplicationsData(Applications);
        setTableRows(mapResponseData(Applications));
    }, [Applications, mapResponseData]);

    return (
        <>
            <div className="w-full">
                <Card type='lg'>
                    {/* Title section */}
                    <div id="title">
                        <div className="flex flex-col sm:justify-between md:justify-between lg:flex-row lg:justify-between w-full">
                            <div className='mb-8'>
                                <h3 className='text-[#042f9c] text-xl font-bold mb-1'>Applications Records Table</h3>
                                <p className='text-[#7F7F80] text-sm'>Displaying {applicationsData.length} of {applicationsData.length} Airtime Record(s)</p>
                            </div>
                        </div>
                    </div>
                    {/* Title section */}

                    <AppTable showSearch={true} tableHeaders={tableHeaders} tableRows={tableRows} />
                
                </Card>
            </div>

            <AppModalComp title=''>
                {
                    modalMode === 'view' && <ApplicationDetailsComp data={selectedRecord} />
                }
                {
                    modalMode === 'update' && <JobApplicationForm mode={modalMode} jobId={selectedRecord ? selectedRecord.job.id : ''} record={selectedRecord}  />
                }
                {
                    modalMode === 'delete' && <DeleteComp id={selectedRecord?.id} action={handleDeleteRecord} deleting={deleting} />
                }
            </AppModalComp>

        <ToastContainer />

        </>
    )
}

export default ApplicationComp;